import { Glasses, Smartphone, Watch, Gift } from 'lucide-react';

/**
 * Hero slide content, kept out of the component so copy and links can be edited
 * without touching carousel logic.
 *
 * `gradient` is the page-level wash behind each slide; `glow` is the coloured
 * pool behind the product image. They are deliberately different intensities —
 * one sets the mood, the other lifts the product off the background.
 *
 * `image` here is only a fallback. The live image comes from the matching
 * category's `image` field, so whoever edits a category in the admin panel also
 * changes the hero — and the hero can never show a different photo from the one
 * on the same category's card further down the page.
 *
 * `slug` is what ties a slide to its category.
 */
export const HERO_SLIDES = [
  {
    id: 'eyewear',
    slug: 'optical',
    eyebrow: 'Optical',
    icon: Glasses,
    headline: 'Frames that earn a second look',
    description:
      'Prescription and sunwear from names you already trust — fitted, checked and dispatched with a brand warranty.',
    primary: { label: 'Shop eyewear', to: '/category/optical' },
    secondary: { label: 'Browse all', to: '/products' },
    image: 'https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=1200&q=80',
    alt: 'Premium eyewear on a neutral background',
    gradient: 'from-[#eef4fc] via-[#f6f9fd] to-white',
    glow: 'bg-primary/25',
  },
  {
    id: 'smartphones',
    slug: 'mobile',
    eyebrow: 'Mobile',
    icon: Smartphone,
    headline: 'The latest flagships, honestly priced',
    description:
      'Sealed retail units with full manufacturer warranty. No refurbished stock quietly mixed in.',
    primary: { label: 'Shop smartphones', to: '/category/mobile' },
    secondary: { label: 'Compare deals', to: '/products?sort=newest' },
    image: 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=1200&q=80',
    alt: 'Modern smartphone held in hand',
    gradient: 'from-[#f1f0f6] via-[#f8f8fb] to-white',
    glow: 'bg-secondary/20',
  },
  {
    id: 'watches',
    slug: 'watch',
    eyebrow: 'Watches',
    icon: Watch,
    headline: 'Time, properly kept',
    description:
      'Analogue and smart watches from established houses, each with its own warranty card in the box.',
    primary: { label: 'Shop watches', to: '/category/watch' },
    secondary: { label: 'New arrivals', to: '/products?sort=newest' },
    image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=1200&q=80',
    alt: 'Wristwatch resting on a surface',
    gradient: 'from-[#f5f1ec] via-[#faf8f5] to-white',
    glow: 'bg-brand-orange/25',
  },
  {
    id: 'gift-cards',
    slug: 'gift-cards',
    eyebrow: 'Gift Cards',
    icon: Gift,
    headline: 'A gift that always fits',
    description:
      'Vouchers from 270+ brands, delivered the moment payment clears. Nothing to ship, nothing to return.',
    primary: { label: 'Browse gift cards', to: '/category/gift-cards' },
    secondary: { label: 'How it works', to: '/faq' },
    image: 'https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=1200&q=80',
    alt: 'Wrapped gift with ribbon',
    gradient: 'from-[#fdf1f4] via-[#fdf7f8] to-white',
    glow: 'bg-brand-pink/20',
  },
];

export const AUTOPLAY_MS = 5000;
